import React, { useCallback, useEffect, useRef, useState } from "react";

const MIN_SCALE = 1;
const MAX_SCALE = 8;
const SCALE_STEP = 0.2;
const PAN_STEP = 52;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const normalizeScale = (value) => Math.round(value * 100) / 100;

export default function ImageInspectorModal({
  visible,
  src,
  title = "Guest ID Inspector",
  alt = "Guest ID",
  onClose,
}) {
  const stageRef = useRef(null);
  const dragRef = useRef(null);
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const resetView = useCallback(() => {
    setView({ scale: 1, x: 0, y: 0 });
    setIsDragging(false);
    dragRef.current = null;
  }, []);

  useEffect(() => {
    if (visible) {
      resetView();
    }
  }, [visible, src, resetView]);

  useEffect(() => {
    if (!visible) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
        return;
      }

      if (event.key === "+" || event.key === "=") {
        setView((current) => ({ ...current, scale: normalizeScale(clamp(current.scale + SCALE_STEP, MIN_SCALE, MAX_SCALE)) }));
      } else if (event.key === "-" || event.key === "_") {
        setView((current) => ({ ...current, scale: normalizeScale(clamp(current.scale - SCALE_STEP, MIN_SCALE, MAX_SCALE)) }));
      } else if (event.key === "0") {
        resetView();
      } else if (event.key === "ArrowLeft") {
        setView((current) => ({ ...current, x: current.x + PAN_STEP }));
      } else if (event.key === "ArrowRight") {
        setView((current) => ({ ...current, x: current.x - PAN_STEP }));
      } else if (event.key === "ArrowUp") {
        setView((current) => ({ ...current, y: current.y + PAN_STEP }));
      } else if (event.key === "ArrowDown") {
        setView((current) => ({ ...current, y: current.y - PAN_STEP }));
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [visible, onClose, resetView]);

  const zoomTo = (nextScale, anchorPoint) => {
    setView((current) => {
      const scale = normalizeScale(clamp(nextScale, MIN_SCALE, MAX_SCALE));

      if (!anchorPoint || !stageRef.current || scale === current.scale) {
        return {
          ...current,
          scale,
        };
      }

      const rect = stageRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const relativeX = anchorPoint.x - centerX;
      const relativeY = anchorPoint.y - centerY;

      const worldX = (relativeX - current.x) / current.scale;
      const worldY = (relativeY - current.y) / current.scale;

      return {
        scale,
        x: relativeX - worldX * scale,
        y: relativeY - worldY * scale,
      };
    });
  };

  const zoomIn = () => zoomTo(view.scale + SCALE_STEP);
  const zoomOut = () => zoomTo(view.scale - SCALE_STEP);

  const onWheel = (event) => {
    if (!src) return;
    event.stopPropagation();
    const delta = event.deltaY < 0 ? SCALE_STEP : -SCALE_STEP;
    zoomTo(view.scale + delta, { x: event.clientX, y: event.clientY });
  };

  const onPointerDown = (event) => {
    if (event.button !== 0 || !stageRef.current) return;

    stageRef.current.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: view.x,
      originY: view.y,
    };
    setIsDragging(true);
  };

  const onPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;

    setView((current) => ({
      ...current,
      x: drag.originX + dx,
      y: drag.originY + dy,
    }));
  };

  const onPointerUp = (event) => {
    if (!stageRef.current) return;

    if (dragRef.current && dragRef.current.pointerId === event.pointerId) {
      dragRef.current = null;
      setIsDragging(false);
    }

    if (stageRef.current.hasPointerCapture(event.pointerId)) {
      stageRef.current.releasePointerCapture(event.pointerId);
    }
  };

  const onDoubleClick = (event) => {
    if (view.scale === 1) {
      zoomTo(2, { x: event.clientX, y: event.clientY });
      return;
    }

    resetView();
  };

  if (!visible) return null;

  return (
    <div className="image-inspector-backdrop" onClick={onClose} role="presentation">
      <section
        className="image-inspector-shell"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="image-inspector-toolbar">
          <div className="image-inspector-titleWrap">
            <strong>{title}</strong>
            <span>{Math.round(view.scale * 100)}%</span>
          </div>
          <div className="image-inspector-actions">
            <button type="button" className="image-inspector-btn" onClick={zoomOut} disabled={view.scale <= MIN_SCALE} aria-label="Zoom out">
              <i className="pi pi-search-minus" aria-hidden="true" />
            </button>
            <button type="button" className="image-inspector-btn" onClick={zoomIn} disabled={view.scale >= MAX_SCALE} aria-label="Zoom in">
              <i className="pi pi-search-plus" aria-hidden="true" />
            </button>
            <button type="button" className="image-inspector-btn image-inspector-btn-text" onClick={resetView} aria-label="Reset view">
              Reset
            </button>
            <button type="button" className="image-inspector-btn" onClick={onClose} aria-label="Close">
              <i className="pi pi-times" aria-hidden="true" />
            </button>
          </div>
        </header>

        <div
          ref={stageRef}
          className={`image-inspector-stage ${isDragging ? "dragging" : ""}`}
          onWheel={onWheel}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onDoubleClick={onDoubleClick}
        >
          {src ? (
            <img
              src={src}
              alt={alt}
              className="image-inspector-image"
              draggable={false}
              referrerPolicy="no-referrer"
              style={{
                transform: `translate3d(${view.x}px, ${view.y}px, 0) scale(${view.scale})`,
              }}
              onContextMenu={(event) => event.preventDefault()}
            />
          ) : (
            <div className="image-inspector-empty">Image is unavailable.</div>
          )}
        </div>

        <footer className="image-inspector-footer">
          Drag to pan · Scroll to zoom · Double-click to toggle zoom · ESC to close
        </footer>
      </section>
    </div>
  );
}
